// Página Single da Nota Fiscal - Preview e Informações
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Badge } from '../../../components/common/Badge';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { ArrowLeft, Download, Mail, Printer, FileText, AlertCircle } from 'lucide-react';
import { getNotaFiscalById } from '../../../services/notasFiscaisService';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import type { NotaFiscal } from '../../../types';

export function NotaSingle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [nota, setNota] = useState<NotaFiscal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (id) {
      carregarNota();
    }
  }, [id]);

  const carregarNota = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const notaData = await getNotaFiscalById(id);
      
      if (!notaData) {
        setError('Nota fiscal não encontrada');
      } else {
        setNota(notaData);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar nota fiscal');
      console.error('Erro ao carregar nota fiscal:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Consultas', path: '/app/consultas' }, { label: 'Nota Fiscal' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !nota) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Consultas', path: '/app/consultas' }, { label: 'Nota Fiscal' }]} />
        <Card>
          <div className="text-center py-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-gray-600 dark:text-gray-400">{error || 'Nota fiscal não encontrada'}</p>
            </div>
            <Button onClick={() => navigate('/app/consultas')} className="mt-4">
              Voltar para Consultas
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Consultas', path: '/app/consultas' }, { label: `Nota ${nota.numero}/${nota.serie}` }]} />
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate('/app/consultas')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Nota Fiscal {nota.numero}/{nota.serie}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          <Button variant="secondary">
            <Mail className="w-4 h-4 mr-2" />
            Enviar por Email
          </Button>
          <Button variant="secondary">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
        </div>
      </div>
      
      {/* Layout de duas colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna Esquerda - Informações Vinculadas */}
        <div className="space-y-6">
          {/* Informações Gerais */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Informações do Documento
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tipo</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{nota.tipo}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <Badge
                    variant={
                      nota.status === 'emitida' ? 'success' :
                      nota.status === 'cancelada' ? 'danger' :
                      nota.status === 'rascunho' ? 'warning' : 'default'
                    }
                  >
                    {nota.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Número</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{nota.numero}/{nota.serie}</p>
                </div>
                {nota.chaveAcesso && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Chave de Acesso</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-xs break-all">{nota.chaveAcesso}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Data de Emissão</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(nota.dataEmissao)}</p>
                </div>
                {nota.dataVencimento && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Data de Vencimento</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(nota.dataVencimento)}</p>
                  </div>
                )}
              </div>
              {nota.formaPagamento && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Forma de Pagamento</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{nota.formaPagamento}</p>
                </div>
              )}
            </div>
          </Card>
          
          {/* Emitente */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Emitente
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Razão Social</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.emitente.razaoSocial}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">CNPJ</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.emitente.cnpj}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Endereço</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {nota.emitente.endereco.logradouro}, {nota.emitente.endereco.numero} - {nota.emitente.endereco.bairro}
                  <br />
                  {nota.emitente.endereco.cidade}/{nota.emitente.endereco.estado} - CEP: {nota.emitente.endereco.cep}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.emitente.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Telefone</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.emitente.telefone}</p>
              </div>
            </div>
          </Card>
          
          {/* Destinatário/Tomador */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {nota.tipo === 'NFSe' ? 'Tomador' : 'Destinatário'}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Nome</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.destinatario.nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{nota.destinatario.tipo === 'F' ? 'CPF' : 'CNPJ'}</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {nota.destinatario.tipo === 'F' ? nota.destinatario.cpf : nota.destinatario.cnpj}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Endereço</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {nota.destinatario.endereco.logradouro}, {nota.destinatario.endereco.numero} - {nota.destinatario.endereco.bairro}
                  <br />
                  {nota.destinatario.endereco.cidade}/{nota.destinatario.endereco.estado} - CEP: {nota.destinatario.endereco.cep}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.destinatario.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Telefone</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{nota.destinatario.telefone}</p>
              </div>
            </div>
          </Card>
          
          {/* Itens */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Itens
            </h2>
            <div className="space-y-4">
              {nota.itens.map((item, index) => (
                <div key={item.id || index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{item.descricao}</p>
                      {item.ncm && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">NCM: {item.ncm}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Quantidade</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{item.quantidade} {item.unidadeMedida}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Valor Unitário</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.valorUnitario)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Total</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.valorTotal)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          
          {/* Totais */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Totais
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(nota.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(nota.desconto)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Total:</span>
                <span className="text-lg font-bold text-primary-600 dark:text-primary-400">{formatCurrency(nota.total)}</span>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Coluna Direita - Preview da Nota Fiscal */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <Card className="bg-white dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Preview da Nota Fiscal
            </h2>
            <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-900">
              {/* Cabeçalho da Nota */}
              <div className="text-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {nota.tipo === 'NFe' ? 'NOTA FISCAL ELETRÔNICA' : nota.tipo === 'NFSe' ? 'NOTA FISCAL DE SERVIÇOS ELETRÔNICA' : 'NOTA FISCAL DE CONSUMIDOR ELETRÔNICA'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Nº {nota.numero} - Série {nota.serie}
                </p>
              </div>
              
              {/* Informações Principais */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Data de Emissão:</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(nota.dataEmissao)}</p>
                  </div>
                  {nota.chaveAcesso && (
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Chave de Acesso:</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-xs break-all">{nota.chaveAcesso}</p>
                    </div>
                  )}
                </div>
                
                {/* Emitente */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">EMITENTE</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{nota.emitente.razaoSocial}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">CNPJ: {nota.emitente.cnpj}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {nota.emitente.endereco.logradouro}, {nota.emitente.endereco.numero} - {nota.emitente.endereco.bairro}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {nota.emitente.endereco.cidade}/{nota.emitente.endereco.estado} - CEP: {nota.emitente.endereco.cep}
                  </p>
                </div>
                
                {/* Destinatário */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {nota.tipo === 'NFSe' ? 'TOMADOR' : 'DESTINATÁRIO'}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{nota.destinatario.nome}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {nota.destinatario.tipo === 'F' ? 'CPF' : 'CNPJ'}: {nota.destinatario.tipo === 'F' ? nota.destinatario.cpf : nota.destinatario.cnpj}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {nota.destinatario.endereco.logradouro}, {nota.destinatario.endereco.numero} - {nota.destinatario.endereco.bairro}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {nota.destinatario.endereco.cidade}/{nota.destinatario.endereco.estado} - CEP: {nota.destinatario.endereco.cep}
                  </p>
                </div>
              </div>
              
              {/* Itens */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">ITENS</p>
                <div className="space-y-3">
                  {nota.itens.map((item, index) => (
                    <div key={item.id || index} className="text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{item.descricao}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.valorTotal)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                        <span>Qtd: {item.quantidade} {item.unidadeMedida} × {formatCurrency(item.valorUnitario)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Totais */}
              <div className="border-t-2 border-gray-300 dark:border-gray-600 pt-4">
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(nota.subtotal)}</span>
                </div>
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(nota.desconto)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="font-bold text-gray-900 dark:text-gray-100">TOTAL:</span>
                  <span className="font-bold text-lg text-primary-600 dark:text-primary-400">{formatCurrency(nota.total)}</span>
                </div>
              </div>
              
              {/* Status */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
                <Badge
                  variant={
                    nota.status === 'emitida' ? 'success' :
                    nota.status === 'cancelada' ? 'danger' :
                    nota.status === 'rascunho' ? 'warning' : 'default'
                  }
                  className="text-sm"
                >
                  {nota.status.toUpperCase()}
                </Badge>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
